import { NavItemType } from "./NavigationTypes";
import {
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import NavItem from "./NavItem";
import React, { useEffect, useState } from "react";
import { NavGroupStyles } from "../styles/NavigationStyles";
import { AppendIfPrefixExists } from "../../../../modules/sitePaths";
import { NavLink, useLocation } from "react-router-dom";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";

interface NavDashboardProps {
  navItem: NavItemType;
  tier: number;
  parentContext: string;
}
export default function NavDashboard({
  navItem,
  tier,
  parentContext,
}: NavDashboardProps) {
  const theme = useTheme();
  const styles = NavGroupStyles(theme);
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const hasActiveParentPath = navItem.path
      ? handleCheckPathName(navItem.path)
      : false;
    const hasActiveVisibleChildPath = hasActiveChildPath(navItem.children);

    setOpen(hasActiveParentPath || hasActiveVisibleChildPath);
  }, [location.pathname, navItem.path, navItem.children]);

  const handleCheckPathName = (path: string) => {
    return (
      location.pathname.startsWith(path) ||
      location.pathname.startsWith(AppendIfPrefixExists(path))
    );
  };

  const hasActiveChildPath = (children?: (NavItemType | undefined)[]) => {
    if (!children || children.length === 0) {
      return false;
    }

    return children.some((child) => {
      if (!child?.visible) {
        return false;
      }

      if (child.path && handleCheckPathName(child.path)) {
        return true;
      }

      return hasActiveChildPath(child.children);
    });
  };

  const handleDropDownChange = (
    e: React.MouseEvent<SVGSVGElement, MouseEvent>,
  ) => {
    e.preventDefault();
    setOpen(!open);
  };
  const hasVisibleChildren =
    navItem.children && navItem.children.some((child) => child?.visible);

  const telemetryContext = `${parentContext} > ${navItem.displayName}`;
  return (
    <>
      <ListItemButton
        id={navItem.id}
        component={NavLink}
        to={AppendIfPrefixExists(navItem.path)}
        {...telemetryProps({
          name: "nav_dashboard",
          section: "navigation",
          label: telemetryContext,
          id: navItem.id,
        })}
        onClick={() => setOpen(!open)}
        sx={{
          "&.active": {
            bgcolor: "action.selected",
          },
          "&.Mui-focusVisible": {
            bgcolor: "primary.main",
            color: "primary.contrastText",
            outline: "2px solid",
            outlineColor: "primary.dark",
            outlineOffset: "-2px",
            "& .MuiListItemIcon-root": {
              color: "inherit",
            },
          },
        }}
      >
        {navItem.icon && (
          <ListItemIcon sx={styles.listItemIcon}>{navItem.icon}</ListItemIcon>
        )}
        <ListItemText
          primary={navItem.displayName}
          sx={[
            tier === 1 ? styles.listItemText : {},
            tier === 2 ? styles.tier2 : {},
          ]}
          title={navItem.displayName}
        />
        {hasVisibleChildren ? (
          open ? (
            <ExpandLess
              color={"action"}
              onClick={(e) => handleDropDownChange(e)}
            />
          ) : (
            <ExpandMore
              color={"action"}
              onClick={(e) => handleDropDownChange(e)}
            />
          )
        ) : (
          <></>
        )}
      </ListItemButton>
      {navItem.children && navItem.children.length > 0 && (
        <>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {navItem.children.map((child, i) => {
                if (child) {
                  return (
                    <NavItem
                      key={i}
                      navItem={child}
                      tier={tier + 1}
                      parentContext={telemetryContext}
                    />
                  );
                }
              })}
            </List>
          </Collapse>
        </>
      )}
    </>
  );
}
